import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  FileSpreadsheet,
  AlertTriangle,
  Loader2,
  Calendar,
  ClipboardList,
  Clock,
  CheckCircle2,
  Wallet,
  XCircle,
} from 'lucide-react';
import { fetchTableLoans, fetchGroups } from '../Users/api';

// The 5 status cards that replace the old status <select>. `match` decides
// which loans count toward a card; "Cancelled / Rejected" intentionally
// combines two statuses into one bucket, same as requested.
const STATUS_CARDS = [
  { key: 'all', label: 'All Applications', icon: ClipboardList },
  { key: 'pending', label: 'Pending Review', icon: Clock },
  { key: 'approved', label: 'Approved Loans', icon: CheckCircle2 },
  { key: 'disbursed', label: 'Disbursed', icon: Wallet },
  { key: 'cancelled_rejected', label: 'Cancelled / Rejected', icon: XCircle },
];

const matchesCard = (status, cardKey) => {
  switch (cardKey) {
    case 'all': return true;
    case 'pending': return status === 'pending';
    case 'approved': return status === 'approved';
    case 'disbursed': return status === 'disbursed';
    case 'cancelled_rejected': return status === 'cancelled' || status === 'rejected';
    default: return false;
  }
};

// Tailwind accent classes per card, reused for the card icon and for the
// card's active/selected state.
const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  pending: {
    icon: (isDark) => (isDark ? 'text-orange-400' : 'text-orange-600'),
    active: (isDark) => (isDark ? 'border-orange-500 bg-orange-900/20' : 'border-orange-500 bg-orange-50'),
  },
  approved: {
    icon: (isDark) => (isDark ? 'text-blue-400' : 'text-blue-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  disbursed: {
    icon: (isDark) => (isDark ? 'text-green-400' : 'text-green-600'),
    active: (isDark) => (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50'),
  },
  cancelled_rejected: {
    icon: (isDark) => (isDark ? 'text-red-400' : 'text-red-600'),
    active: (isDark) => (isDark ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50'),
  },
};

// The officer who finalized a loan (approved/disbursed/rejected/cancelled
// it) — same fallback the old "ACTION BY" column used. This is what backs
// the new Officer filter dropdown.
const getActionOfficer = (loan) => loan.actionOfficerName || loan.officerName || null;

// Default date range: past 60 days, as requested. "Clear Filter" (in the
// month menu / custom date dialog) resets back to no date restriction.
const getPast60DaysRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 60);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const LoansTableView = ({ isDark }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // which card is active
  const [groupFilter, setGroupFilter] = useState('all');
  const [officerFilter, setOfficerFilter] = useState('all');
  const [groups, setGroups] = useState([]);
  const [dateRange, setDateRange] = useState(getPast60DaysRange());
  // Tracks which single date control is "active" so exactly one of
  // Past 60 Days / Select Month / Custom is highlighted at a time.
  const [activeDatePreset, setActiveDatePreset] = useState('past60'); // 'past60' | 'month' | 'custom' | 'all'
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  // Status is now a client-side card selection, not a server-side filter —
  // we need every status's loans in hand at once to show all 5 card
  // counts, so only the date range triggers a re-fetch.
  useEffect(() => {
    loadGroups();
    fetchAllLoans();
  }, [dateRange]);

  const loadGroups = async () => {
    try {
      const groupsData = await fetchGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const handleCustomDateApply = () => {
    setDateRange(tempDateRange);
    setSelectedMonth('');
    setActiveDatePreset('custom');
    setCustomDateDialogOpen(false);
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedMonth('');
    setActiveDatePreset('all');
  };

  const handlePast60Days = () => {
    setDateRange(getPast60DaysRange());
    setSelectedMonth('');
    setActiveDatePreset('past60');
  };

  const fetchAllLoans = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchTableLoans(dateRange.startDate, dateRange.endDate, 'all');

      if (response.success && response.data) {
        setLoans(response.data);
      } else {
        setLoans([]);
      }
    } catch (err) {
      console.error('Error fetching loans:', err);
      setError('Failed to load loans data');
    } finally {
      setLoading(false);
    }
  };

  // Everything except the status card: search, group, officer, date range.
  // Cards are computed from this so all 5 counts reflect the same
  // search/group/date/officer selection, per bucket.
  const baseFiltered = useMemo(() => {
    let filtered = [...loans];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(loan =>
        loan.member?.name?.toLowerCase().includes(query) ||
        loan.group?.name?.toLowerCase().includes(query) ||
        loan.id?.toString().includes(query)
      );
    }

    if (groupFilter !== 'all') {
      filtered = filtered.filter(loan => loan.group?.id?.toString() === groupFilter);
    }

    if (officerFilter !== 'all') {
      filtered = filtered.filter(loan => getActionOfficer(loan) === officerFilter);
    }

    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(loan => {
        let loanDate;
        switch (loan.status) {
          case 'pending':   loanDate = loan.appliedAt || loan.createdAt; break;
          case 'approved':  loanDate = loan.approvedAt; break;
          case 'disbursed': loanDate = loan.disbursementDate || loan.disbursedAt; break;
          case 'rejected':  loanDate = loan.rejectedAt; break;
          case 'cancelled': loanDate = loan.cancelledAt; break;
          default:          loanDate = loan.createdAt;
        }
        if (!loanDate) return false;
        const date = new Date(loanDate);
        const start = dateRange.startDate ? new Date(dateRange.startDate) : null;
        const end   = dateRange.endDate   ? new Date(dateRange.endDate)   : null;
        if (start && end) return date >= start && date <= end;
        if (start) return date >= start;
        if (end)   return date <= end;
        return true;
      });
    }

    return filtered;
  }, [loans, searchQuery, groupFilter, officerFilter, dateRange]);

  // One count + total-principal figure per card, all computed off the same
  // baseFiltered set so they only differ by status bucket.
  const cardStats = useMemo(() => {
    return STATUS_CARDS.map(card => {
      const matching = baseFiltered.filter(loan => matchesCard(loan.status, card.key));
      const total = matching.reduce((sum, loan) => sum + parseFloat(loan.principalAmount || 0), 0);
      return { ...card, count: matching.length, total };
    });
  }, [baseFiltered]);

  // What actually shows in the table: baseFiltered narrowed to whichever
  // card is currently selected.
  const filteredLoans = useMemo(
    () => baseFiltered.filter(loan => matchesCard(loan.status, statusFilter)),
    [baseFiltered, statusFilter]
  );

  // Officer dropdown options, derived from the same field the old
  // "ACTION BY" column displayed.
  const officerOptions = useMemo(() => {
    const set = new Set();
    loans.forEach(loan => {
      const officer = getActionOfficer(loan);
      if (officer) set.add(officer);
    });
    return Array.from(set).sort();
  }, [loans]);

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return isDark ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-800';
      case 'approved': return isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'disbursed': return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
      case 'rejected': return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800';
      case 'cancelled': return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
      default: return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return `KSh ${parseFloat(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateTotals = () => {
    return filteredLoans.reduce((acc, loan) => {
      const principal = parseFloat(loan.principalAmount || 0);
      const repayment = parseFloat(loan.repaymentAmount || 0);
      const interest = repayment - principal;

      acc.principal += principal;
      acc.interest += interest;
      acc.totalRepayment += repayment;
      return acc;
    }, { principal: 0, interest: 0, totalRepayment: 0 });
  };

  const totals = calculateTotals();

  const exportToExcel = async () => {
    try {
      setLoading(true);
      const { exportLoansToExcel } = await import('../Users/api');

      // NOTE: statusFilter can be 'cancelled_rejected' (the combined card),
      // which the export endpoint may not recognize as a single status —
      // sending both values lets the backend decide how to interpret an
      // OR filter. Confirm this shape against exportLoansToExcel's actual
      // contract on the backend.
      const status = statusFilter === 'cancelled_rejected'
        ? ['cancelled', 'rejected']
        : statusFilter;

      const filters = {
        status,
        groupId: groupFilter,
        officer: officerFilter,
        searchQuery: searchQuery,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      const blob = await exportLoansToExcel(filters);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `loans_report_${timestamp}.xlsx`);

      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export loans to Excel. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading loans...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button
          onClick={fetchAllLoans}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pb-32">
      {/* Status cards - replace the old status <select>. Click a card to
          filter the table by that status; "All Applications" is the default. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {cardStats.map((card) => {
          const Icon = card.icon;
          const isActive = statusFilter === card.key;
          const accent = CARD_ACCENT[card.key];
          return (
            <button
              key={card.key}
              onClick={() => setStatusFilter(card.key)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : isDark
                    ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {card.count}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatCurrency(card.total)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main panel: title, filters, table and footer count all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`flex items-start justify-between gap-4 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Long-term Loans
            </h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Every loan application, grouped by status.
            </p>
          </div>
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shrink-0"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </button>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search loans..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Group Filter */}
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Groups</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>

          {/* Officer Filter - options come from the loans' "action by" officer,
              same field the removed ACTION BY column used to display. */}
          <select
            value={officerFilter}
            onChange={(e) => setOfficerFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Officers</option>
            {officerOptions.map(officer => (
              <option key={officer} value={officer}>
                {officer}
              </option>
            ))}
          </select>

          {/* Past 60 Days - the default range. Highlighted whenever it's the
              active preset; clicking it re-activates it over Month/Custom. */}
          <button
            onClick={handlePast60Days}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'past60'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Past 60 Days
          </button>

          {/* Month Selector Button */}
          <button
            onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
            className={`px-4 py-2 border rounded-lg transition-colors text-left flex items-center justify-between ${
              activeDatePreset === 'month'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <span>
              {selectedMonth !== '' ? (() => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const [year, monthIndex] = selectedMonth.split('-');
                return `${monthNames[parseInt(monthIndex)]} ${year}`;
              })() : 'Select Month'}
            </span>
            <Calendar className="w-4 h-4 ml-2" />
          </button>

          {/* Custom Date Button */}
          <button
            onClick={() => {
              setTempDateRange(dateRange);
              setCustomDateDialogOpen(true);
            }}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'custom'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  GROUP NAME
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  CLIENT NAME
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  PRINCIPAL
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  INSTALLMENTS
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  INTEREST
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  TOTAL REPAYMENT
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  APPLIED BY
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  STATUS
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredLoans.map((loan, index) => {
                const interest = (loan.repaymentAmount || 0) - (loan.principalAmount || 0);

                return (
                  <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {loan.group?.name || 'N/A'}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {formatDate(loan.appliedAt || loan.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {loan.member?.name || 'N/A'}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        ID: {loan.member?.id_number || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-blue-600">
                      {formatCurrency(loan.principalAmount)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium ${
                        isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {loan.installments || 0} months
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-orange-600">
                      {formatCurrency(interest)}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-green-600">
                      {formatCurrency(loan.repaymentAmount)}
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                        {loan.officerName || 'System'}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {formatDate(loan.appliedAt || loan.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(loan.status)}`}>
                        {loan.status || 'Unknown'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <tr>
                <td className={`px-6 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan="2">
                  TOTAL
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  {formatCurrency(totals.principal)}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {filteredLoans.length} loans
                  </span>
                </td>
                <td className="px-6 py-4 text-right font-bold text-orange-600">
                  {formatCurrency(totals.interest)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-green-600">
                  {formatCurrency(totals.totalRepayment)}
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {filteredLoans.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No loans found matching your filters.</p>
          </div>
        )}

        {/* Footer count */}
        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredLoans.length} of {loans.length} loans
        </div>
      </div>

      {/* Month Selection Menu */}
      {monthMenuAnchor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuAnchor(null)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${monthNames[monthIndex]} ${year}`,
                  monthIndex: monthIndex,
                  year: year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    const startDate = new Date(month.year, month.monthIndex, 1);
                    const endDate = new Date(month.year, month.monthIndex + 1, 0);
                    setDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setSelectedMonth(month.value);
                    setActiveDatePreset('month');
                    setMonthMenuAnchor(null);
                  }}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleClearDateFilter}
                className={`w-full px-4 py-2 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                }`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Custom Date Range
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleClearDateFilter}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoansTableView;